<template>
    <div>
        <WorkDocumentUploadDialog
            v-if="visualEnabled"
            v-model:file="pendingWorkDocument"
            :open="isWorkDocumentDialogOpen"
            capabilities-url="/api/chat/visual-document/capabilities"
            :can-add="!isDisabled && remaining > 0"
            :prepare-attachment="persistVisualDocument"
            @added="handleWorkDocumentAdded"
            @read-failed="handleWorkDocumentReadFailed"
            @update:open="handleWorkDocumentDialogOpen"
            @selection-cleared="readerFallbackGeneration += 1"
            @upload-error="reportError" />
        <div class="flex flex-wrap flex-col gap-3">
            <BaseFileUpload
                v-slot="{ clickInput }"
                class="grow"
                :is-disabled="isDisabled || isUploading"
                :accepted-file-types="acceptedTypes"
                is-multiple-file-upload-enabled
                @file-upload="handleFileUpload">
                <BaseCard
                    as="button"
                    type="button"
                    padding="lg"
                    radius="lgr"
                    :disabled="isDisabled || isUploading"
                    class="w-full flex flex-col text-center items-center gap-2"
                    @click="clickInput">
                    <i v-if="!isUploading" class="bi bi-cloud-upload text-2xl/none text-ink-muted" aria-hidden="true"></i>
                    <LoadingSpinner v-else class="h-7.5" />
                    <p class="body-small-semibold whitespace-nowrap">{{ t("moments.documents.additional.dropOrBrowse") }}</p>
                    <p class="text-xs text-ink-muted">{{ sizeLimitsText }}</p>
                </BaseCard>
            </BaseFileUpload>
            <div role="status" class="empty:sr-only">
                <p v-if="notice" class="body-small text-ink-muted">{{ notice }}</p>
            </div>
            <FileAttachedTag
                v-for="file in displayedFiles"
                :key="'id' in file ? file.id : 'draftId' in file ? file.draftId : file.name"
                :file="file"
                is-removable
                @remove="handleFileClear(file)" />
        </div>
        <BaseToast v-model:open="isErrorToastOpen" :description="errorMessage" />
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { SaveVisualReviewDocumentRequest, UploadedFile as SavedFile } from "~vue/api/generated/chat";
import type { ChatInputFile, UploadedFile, UserDetails, VisualDocumentContent } from "~vue/appTypes";
import WorkDocumentUploadDialog from "~vue/ChatDetail/WorkDocumentUploadDialog.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseFileUpload from "~vue/components/BaseFileUpload.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import FileAttachedTag from "~vue/components/FileAttachedTag.vue";
import { useDocumentIntake } from "~vue/composables/useDocumentIntake";
import { useFileUpload } from "~vue/composables/useFileUpload";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { FILE_UPLOAD_LIMIT } from "~vue/utils/constants";
import { formatOversizedFiles, type OversizedFile } from "~vue/utils/fileUpload";
import { formatNumber } from "~vue/utils/formatNumber";
import { computed, inject, onBeforeUnmount, onUnmounted, type Ref, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const sizeLimitsText = computed(() => t("moments.documents.additional.sizeLimits", { largeLimit: formatNumber(6, "decimal"), smallLimit: formatNumber(3, "decimal") }));

interface PerformanceReviewUploadedFile extends UploadedFile {
    upload_type: string;
}

const {
    isDisabled = false,
    uploadType,
    remainingFileCount = FILE_UPLOAD_LIMIT,
    files: controlledFiles,
} = defineProps<{
    isDisabled?: boolean;
    uploadType: string;
    remainingFileCount?: number;
    files?: ChatInputFile[];
}>();

const emit = defineEmits<{
    uploading: [busy: boolean];
    "document-uploaded": [documents: PerformanceReviewUploadedFile[], pasted: boolean];
    "document-removed": [document: ChatInputFile];
    "document-selected": [document: { upload_type: string }];
    "document-upload-error": [document: { upload_type: string }, pasted: boolean];
}>();

const userDetails = inject<Ref<UserDetails | undefined>>("user_details", ref(undefined));
const visualEnabled = computed(() => !!userDetails.value?.is_visual_document_analysis_enabled);
const notice = ref("");
let active = true;
onUnmounted(() => {
    active = false;
});
const additionalFiles = ref<PerformanceReviewUploadedFile[]>([]);
const displayedFiles = computed(() => controlledFiles ?? additionalFiles.value);
const isErrorToastOpen = ref(false);
const errors = ref<string[]>([]);

const errorMessage = computed(() => {
    return errors.value.join("\n");
});

const { uploadFiles, getFileSizeLimits, acceptedFileTypes } = useFileUpload({
    unsupportedFileCallback: (files: File[]) => {
        errors.value.push(t("moments.documents.errors.unsupportedFormats", { files: files.map((f) => f.name).join(", ") }));
    },
    oversizedFileCallback: (files: OversizedFile[]) => {
        errors.value.push(t("moments.documents.errors.oversized", { files: formatOversizedFiles(files) }));
    },
    errorCallback: (error: string) => {
        errors.value.push(error);
    },
});

const remaining = computed(() => Math.min(remainingFileCount, FILE_UPLOAD_LIMIT - displayedFiles.value.length));
const acceptedTypes = computed(() => (visualEnabled.value ? `${acceptedFileTypes.value},.jpg,.jpeg,.png,.webp,.gif` : acceptedFileTypes.value));
const reportError = (message: string) => {
    if (!active) {
        return;
    }
    errors.value.push(message);
    isErrorToastOpen.value = true;
    emit("document-upload-error", { upload_type: uploadType }, false);
};
const attachFiles = (files: UploadedFile[]) => {
    if (!active) {
        return;
    }
    const decorated = files.map((file) => ({ ...file, upload_type: uploadType }));
    if (controlledFiles === undefined) {
        additionalFiles.value.push(...decorated);
    }
    emit("document-uploaded", decorated, false);
};
let savedVisualFile: UploadedFile | null = null;
const persistVisualDocument = async (file: VisualDocumentContent) => {
    const payload: SaveVisualReviewDocumentRequest = { file };
    const { data, error } = await useFetch("/v1/chat/performance-management/documents/visual").post(payload).json<SavedFile>();
    if (error.value || !data.value) {
        throw new Error(t("moments.documents.dialog.linkFailed"));
    }
    if (active) {
        savedVisualFile = data.value;
    }
};
const {
    routeFiles,
    busy: isUploading,
    isWorkDocumentDialogOpen,
    pendingWorkDocument,
    readerFallbackGeneration,
    handleWorkDocumentAdded,
    handleWorkDocumentDialogOpen,
    handleWorkDocumentReadFailed,
} = useDocumentIntake({
    visualEnabled: () => visualEnabled.value,
    uploadEnabled: () => true,
    disabled: () => isDisabled,
    remaining: () => remaining.value,
    legacyLimit: async (file) => {
        const limits = await getFileSizeLimits();
        return limits.limits[file.type] ?? limits.default;
    },
    upload: async (files, _source, fields, isStale) => {
        try {
            const data = await (Object.keys(fields).length ? uploadFiles(files, fields) : uploadFiles(files));
            if (isStale()) {
                return [];
            }
            attachFiles(data.uploadedFiles);
            if (errors.value.length) {
                isErrorToastOpen.value = true;
                emit("document-upload-error", { upload_type: uploadType }, false);
            }
            return data.uploadedFiles.length ? files.map((file) => ({ file })) : [];
        } catch (error) {
            if (!isStale()) {
                reportError(t("moments.documents.errors.uploadFailed", { error: error instanceof Error ? error.message : String(error) }));
            }
            return [];
        }
    },
    addVisual: () => {
        if (savedVisualFile) {
            attachFiles([savedVisualFile]);
        }
        savedVisualFile = null;
    },
    error: reportError,
    notice: (message) => {
        notice.value = message;
    },
});
watch(isUploading, (value) => emit("uploading", value), { flush: "sync" });
onBeforeUnmount(() => emit("uploading", false));
const handleFileUpload = async (files: File[]) => {
    errors.value = [];
    notice.value = "";
    emit("document-selected", { upload_type: uploadType });
    await routeFiles(files, "upload_button");
};

const handleFileClear = (file: ChatInputFile) => {
    if (controlledFiles === undefined && "id" in file) {
        additionalFiles.value = additionalFiles.value.filter((f) => f.id !== file.id);
    }
    emit("document-removed", file);
};
</script>
